import { Trans, t } from "@lingui/macro";
import { useMemo } from "react";

import { ARBITRUM, BSC } from "config/chains";
import { USD_DECIMALS } from "config/factors";
import { getIcons } from "config/icons";
import { MONEY_PRICE_DECIMALS } from "config/ui";
import { useTotalMoneyStaked } from "domain/legacy";
import { bigMath } from "lib/bigmath";
import { MONEY_DECIMALS } from "lib/legacy";
import { expandDecimals, formatAmount, formatTokenAmount, formatUsd } from "lib/numbers";
import { sumBigInts } from "lib/sumBigInts";

import InteractivePieChart from "components/InteractivePieChart/InteractivePieChart";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import TooltipComponent from "components/Tooltip/Tooltip";
import AssetDropdown from "./AssetDropdown";

export function MoneyCard({
  chainId,
  moneyPrice,
  moneyPriceFromArbitrum,
  moneyPriceFromBsc,
  totalMoneySupply,
  moneyMarketCap,
  totalMoneyInLiquidity,
}: {
  chainId: number;
  moneyPrice: bigint | undefined;
  moneyPriceFromArbitrum: bigint | undefined;
  moneyPriceFromBsc: bigint | undefined;
  totalMoneySupply: bigint | undefined;
  moneyMarketCap: bigint | undefined;
  totalMoneyInLiquidity: bigint;
}) {
  const currentIcons = getIcons(chainId)!;

  let { [BSC]: stakedMoneyBsc, [ARBITRUM]: stakedMoneyArbitrum, total: totalStakedMoney } = useTotalMoneyStaked();

  const stakedMoneyArbitrumUsd =
    moneyPriceFromArbitrum !== undefined && stakedMoneyArbitrum !== undefined
      ? bigMath.mulDiv(stakedMoneyArbitrum, moneyPriceFromArbitrum, expandDecimals(1, MONEY_DECIMALS))
      : undefined;
  const stakedMoneyBscUsd =
    moneyPriceFromBsc !== undefined && stakedMoneyBsc !== undefined
      ? bigMath.mulDiv(stakedMoneyBsc, moneyPriceFromBsc, expandDecimals(1, MONEY_DECIMALS))
      : undefined;
  // const totalStakedMoneyUsd = sumBigInts(stakedMoneyArbitrumUsd, stakedMoneyBscUsd);
  const totalStakedMoneyUsd = sumBigInts(stakedMoneyBscUsd);

  let stakedPercent = 0;

  if (totalMoneySupply !== undefined && totalMoneySupply !== 0n && totalStakedMoney !== 0n) {
    stakedPercent = Number(bigMath.mulDiv(totalStakedMoney, 100n, totalMoneySupply));
  }

  let liquidityPercent = 0;

  if (totalMoneySupply !== undefined && totalMoneySupply !== 0n && totalMoneyInLiquidity !== undefined) {
    liquidityPercent = Number(bigMath.mulDiv(totalMoneyInLiquidity, 100n, totalMoneySupply));
  }

  let notStakedPercent = 100 - stakedPercent - liquidityPercent;

  const moneyDistributionData = useMemo(() => {
    let arr = [
      {
        name: t`staked`,
        value: stakedPercent,
        color: "#4353fa",
      },
      {
        name: t`in liquidity`,
        value: liquidityPercent,
        color: "#0598fa",
      },
      {
        name: t`not staked`,
        value: notStakedPercent,
        color: "#5c0af5",
      },
    ];

    return arr;
  }, [liquidityPercent, notStakedPercent, stakedPercent]);

  const formattedTotalStakedMoneyUsd = formatUsd(totalStakedMoneyUsd, { displayDecimals: 0 });
  const formattedStakedMoneyArbitrumUsd = `${formatUsd(stakedMoneyArbitrumUsd, { displayDecimals: 0 })} (${formatTokenAmount(
    stakedMoneyArbitrum,
    MONEY_DECIMALS,
    "MONEY",
    {
      useCommas: true,
      displayDecimals: 0,
    }
  )})`;
  const formattedStakedMoneyBscUsd = `${formatUsd(stakedMoneyBscUsd, { displayDecimals: 0 })} (${formatTokenAmount(
    stakedMoneyBsc,
    MONEY_DECIMALS,
    "MONEY",
    {
      useCommas: true,
      displayDecimals: 0,
    }
  )})`;
  const formattedTotalStakedMoneyUsdWithToken = `${formatUsd(totalStakedMoneyUsd)} (${formatTokenAmount(
    totalStakedMoney,
    MONEY_DECIMALS,
    "MONEY",
    {
      useCommas: true,
      displayDecimals: 0,
    }
  )})`;

  return (
    <div className="App-card">
      <div className="stats-block">
        <div className="App-card-title">
          <div className="App-card-title-mark">
            <div className="App-card-title-mark-icon">
              <img src={currentIcons.money} width="40" alt="MONEY Token Icon" />
            </div>
            <div className="App-card-title-mark-info">
              <div className="App-card-title-mark-title">MONEY</div>
              <div className="App-card-title-mark-subtitle">MONEY</div>
            </div>
            <div>
              <AssetDropdown assetSymbol="MONEY" />
            </div>
          </div>
        </div>
        <div className="App-card-divider"></div>
        <div className="App-card-content">
          <div className="App-card-row">
            <div className="label">
              <Trans>Price</Trans>
            </div>
            <div>
              {moneyPrice === undefined || moneyPrice === 0n ? (
                "..."
              ) : (
                <TooltipComponent
                  position="bottom-end"
                  className="whitespace-nowrap"
                  handle={"$" + formatAmount(moneyPrice, USD_DECIMALS, MONEY_PRICE_DECIMALS, true)}
                  content={
                    <>
                      {/* <StatsTooltipRow
                        label={t`Price on Ronin`}
                        value={formatAmount(moneyPriceFromArbitrum, USD_DECIMALS, MONEY_PRICE_DECIMALS, true)}
                        showDollar={true}
                      /> */}
                      <StatsTooltipRow
                        label={t`Price on Bsc`}
                        value={formatAmount(moneyPriceFromBsc, USD_DECIMALS, MONEY_PRICE_DECIMALS, true)}
                        showDollar={true}
                      />
                    </>
                  }
                />
              )}
            </div>
          </div>
          <div className="App-card-row">
            <div className="label">
              <Trans>Total Supply</Trans>
            </div>
            <div>
              <TooltipComponent
                position="bottom-end"
                handle={`${formatAmount(totalMoneySupply, MONEY_DECIMALS, 0, true)} MONEY`}
                content={t`Total circulating supply of MONEY tokens.`}
              />
            </div>
          </div>
          <div className="App-card-row">
            <div className="label">
              <Trans>Total Staked</Trans>
            </div>
            <div>
              <TooltipComponent
                position="bottom-end"
                tooltipClassName="!max-w-[450px]"
                handle={formattedTotalStakedMoneyUsd}
                content={
                  <>
                    {/* <StatsTooltipRow
                      label={t`Staked on Ronin`}
                      value={formattedStakedMoneyArbitrumUsd}
                      showDollar={false}
                    /> */}
                    <StatsTooltipRow
                      label={t`Staked on Bsc`}
                      value={formattedStakedMoneyBscUsd}
                      showDollar={false}
                    />
                    <div className="!my-8 h-1 bg-gray-800" />
                    <StatsTooltipRow label={t`Total`} value={formattedTotalStakedMoneyUsdWithToken} showDollar={false} />
                  </>
                }
              />
            </div>
          </div>
          <div className="App-card-row">
            <div className="label">
              <Trans>Market Cap</Trans>
            </div>
            <div>${formatAmount(moneyMarketCap, USD_DECIMALS, 0, true)}</div>
          </div>
        </div>
      </div>
      <InteractivePieChart data={moneyDistributionData} label={t`Distribution`} />
    </div>
  );
}
